import { useEffect, useMemo, useState } from "react";
import { FieldArray, useFormikContext } from "formik";
import * as Yup from "yup";
import { Menu } from "@/types/menus";
import { Button, IconButton, MenuItem, Select, Stack, TextField, Accordion, AccordionSummary, AccordionDetails, Typography, FormControl, InputLabel } from "@mui/material";
import { EmptyState } from "@/components";
import { Add } from "@mui/icons-material";
import { GripHorizontal, Trash2 } from "lucide-react";
import { useAppSelector } from "@/hooks";
import { useAccessControl } from "@/features/access-control";
import { useFetchTemplateByIdQuery } from "@/features/templates/templatesApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { DragDropContext, Droppable, Draggable, DropResult, DraggingStyle, NotDraggingStyle } from "react-beautiful-dnd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Category, OrgCategory } from "@/types";
import useOrganizations from "@/features/organizations/useOrganizations";

const EMPTY_SECTION = {
  category: "",
  categoryTitle: "",
};

const MenuStructureTab = () => {
  const { hasAccess } = useAccessControl();
  const { values, handleChange, setFieldValue } = useFormikContext<Menu>();
  const categories = useAppSelector((state) => state.categories);
  const { currentOrganization } = useOrganizations();

  const template = useFetchTemplateByIdQuery(values?.template || skipToken);
  const [expandedAccordion, setExpandedAccordion] = useState<number | undefined>();

  const allCategories = useMemo(
    () =>
      [...(Object.values(categories ?? {}) as Category[]).filter((c) => c.industry === currentOrganization?.industry), ...(currentOrganization?.categories ?? [])] as
        | OrgCategory[]
        | Category[],
    [categories, currentOrganization]
  );

  const sortedCategories = useMemo(() => {
    return allCategories.sort((a, b) => {
      if (a.title < b.title) {
        return -1;
      }
      if (a.title > b.title) {
        return 1;
      }
      return 0;
    });
  }, [allCategories]);

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) return;
    if (!values?.structure?.length) return;
    if (JSON.stringify(source) === JSON.stringify(destination)) return;

    const newStructure = [...values.structure];
    const [reorderedItem] = newStructure.splice(source.index, 1);
    newStructure.splice(destination.index, 0, reorderedItem);
    setFieldValue("structure", newStructure);

    if (source.index === expandedAccordion) {
      setExpandedAccordion(destination.index);
    }
  };

  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? "rgba(0, 0, 0, 0.05)" : "transparent",
  });

  const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle | undefined) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    boxShadow: isDragging ? "0px 8px 16px rgba(0, 0, 0, 0.1)" : "none",
    // change background colour if dragging
    background: "white",
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  useEffect(() => {
    if (!values?.structure && template.data?.structure) {
      setFieldValue("structure", template.data.structure);
    }
  }, [setFieldValue, template.data, values?.structure]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <FieldArray
        name="structure"
        render={(arrayHelpers) => (
          <Stack gap={2}>
            {values?.structure?.length ? (
              <Stack>
                <Droppable droppableId="menu-structure-droppable">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        ...getListStyle(snapshot.isDraggingOver),
                      }}
                    >
                      {values?.structure?.map((section, index) => (
                        <Draggable key={section?.category} draggableId={section?.category} index={index}>
                          {(provided, snapshot) => (
                            <Accordion
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              direction="row"
                              sx={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                              expanded={expandedAccordion === index}
                              onClick={() => {
                                setExpandedAccordion((prevExpanded) => (prevExpanded === index ? undefined : index));
                              }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header" component="div">
                                <Stack gap={2} direction="row" alignItems="center" flex={1}>
                                  <IconButton {...provided.dragHandleProps}>
                                    <GripHorizontal size={22} />
                                  </IconButton>
                                  <Typography sx={{ width: "100%" }} component="span">
                                    {section.categoryTitle}
                                  </Typography>
                                  <IconButton onClick={() => arrayHelpers.remove(index)} disabled={!hasAccess("delete.menu.structure")}>
                                    <Trash2 size={22} />
                                  </IconButton>
                                </Stack>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack spacing={2} direction="row">
                                  <FormControl fullWidth>
                                    <InputLabel id={`section-category-${index}`}>Categoría</InputLabel>
                                    <Select
                                      label="Categoría"
                                      labelId={`section-category-${index}`}
                                      size="small"
                                      name={`structure.${index}.category`}
                                      value={section.category}
                                      onChange={(e) => {
                                        const draft = [...(values?.structure ?? [])];
                                        const selectedCategory = allCategories.find((c) => c.id === e.target.value);
                                        const { id, title } = selectedCategory || {};
                                        draft.splice(index, 1, {
                                          category: id,
                                          categoryTitle: title,
                                        });
                                        setFieldValue("structure", draft);
                                      }}
                                      disabled={!hasAccess("update.menu.structure")}
                                    >
                                      {sortedCategories.map((c: any) => (
                                        <MenuItem
                                          key={c.id}
                                          value={c.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          {c.title}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  <TextField
                                    label="Título"
                                    size="small"
                                    name={`structure.${index}.categoryTitle`}
                                    value={section.categoryTitle}
                                    onChange={handleChange}
                                    disabled={!hasAccess("update.menu.structure")}
                                    fullWidth
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  />
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
                {hasAccess("create.menu.structure") ? (
                  <Stack direction="row" justifyContent="center" padding={2}>
                    <Button variant="outlined" size="medium" fullWidth={false} onClick={() => arrayHelpers.push(EMPTY_SECTION)} startIcon={<Add />}>
                      Agregar Sección
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            ) : (
              <Stack spacing={2} padding={"32px 16px"}>
                <EmptyState
                  action={{
                    onClick: () => arrayHelpers.push(EMPTY_SECTION),
                    startIcon: <Add />,
                    children: "Agregar Sección",
                    variant: "outlined",
                  }}
                />
              </Stack>
            )}
          </Stack>
        )}
      />
    </DragDropContext>
  );
};

export default MenuStructureTab;

export const MenuStructureSchema = Yup.object().shape({});
